/**
 * Market Analytics Agent
 * Manages market statistics and price forecasting API calls.
 */

import { useState, useCallback } from 'react';
import APIAgent from './apiAgent';

/**
 * Custom hook for market analytics
 * @returns {Object} Market data and methods
 */
export const useMarketAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [insights, setInsights] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [sellerInsights, setSellerInsights] = useState(null);
  const [listingGuidance, setListingGuidance] = useState(null);
  const [sellerScore, setSellerScore] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aiError, setAiError] = useState(null);

  /**
   * Get market statistics for a location
   * @param {string} city - City name
   * @param {string} state - State abbreviation
   */
  const getMarketStats = useCallback(async (city, state) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await APIAgent.request(
        `/api/market/stats/${city}/${state}`
      );
      setStats(response);
    } catch (err) {
      console.error('Failed to fetch market stats:', err);
      setError(err.message || 'Failed to load market statistics');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get property price estimate
   * @param {Object} propertyData - Property details
   */
  const estimateProperty = useCallback(async (propertyData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await APIAgent.request(
        '/api/market/estimate',
        'POST',
        propertyData
      );
      setEstimate(response);
    } catch (err) {
      console.error('Failed to estimate property:', err);
      setError(err.message || 'Failed to estimate property price');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Compare multiple locations
   * @param {Array} locations - List of locations {city, state}
   */
  const compareLocations = useCallback(async (locations) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await APIAgent.request(
        '/api/market/compare-locations',
        'POST',
        { locations }
      );
      setComparison(response);
    } catch (err) {
      console.error('Failed to compare locations:', err);
      setError(err.message || 'Failed to compare locations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get price forecast for a location
   * @param {string} city - City name
   * @param {string} state - State abbreviation
   */
  const getPriceForecast = useCallback(async (city, state) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await APIAgent.request(
        `/api/market/price-forecast/${city}/${state}`
      );
      setForecast(response);
    } catch (err) {
      console.error('Failed to fetch price forecast:', err);
      setError(err.message || 'Failed to load price forecast');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get market insights for a location
   * @param {string} city - City name
   * @param {string} state - State abbreviation
   */
  const getMarketInsights = useCallback(async (city, state) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await APIAgent.request(
        `/api/market/insights/${city}/${state}`
      );
      setInsights(response);
    } catch (err) {
      console.error('Failed to fetch market insights:', err);
      setError(err.message || 'Failed to load market insights');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get AI-powered market insights from the LLM
   * @param {string} city - City name
   * @param {string} state - State abbreviation
   */
  const getAIInsights = useCallback(async (city, state) => {
    setAiLoading(true);
    setAiError(null);

    try {
      const response = await APIAgent.request(
        `/api/market/ai-insights/${city}/${state}`
      );
      setAiInsights(response);
      return response;
    } catch (err) {
      console.error('Failed to fetch AI market insights:', err);
      setAiError(err.message || 'Failed to load AI market insights');
    } finally {
      setAiLoading(false);
    }
  }, []);

  /**
   * Get seller-focused market insights for a location
   * @param {string} city - City name
   * @param {string} state - State abbreviation
   */
  const getSellerInsights = useCallback(async (city, state) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await APIAgent.request(
        `/api/market/seller-insights/${city}/${state}`
      );
      setSellerInsights(response);
      return response;
    } catch (err) {
      console.error('Failed to fetch seller insights:', err);
      setError(err.message || 'Failed to load seller insights');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get listing price guidance for a seller
   * @param {Object} propertyData - Property details {city, state, bedrooms, bathrooms, square_feet}
   */
  const getListingPriceGuidance = useCallback(async (propertyData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await APIAgent.request(
        '/api/market/listing-price-guidance',
        'POST',
        propertyData
      );
      setListingGuidance(response);
      return response;
    } catch (err) {
      console.error('Failed to fetch listing price guidance:', err);
      setError(err.message || 'Failed to load listing price guidance');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get seller favorability score for a market
   * @param {string} city - City name
   * @param {string} state - State abbreviation
   */
  const getSellerScore = useCallback(async (city, state) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await APIAgent.request(
        `/api/market/seller-score/${city}/${state}`
      );
      setSellerScore(response);
      return response;
    } catch (err) {
      console.error('Failed to fetch seller score:', err);
      setError(err.message || 'Failed to load seller score');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearAll = useCallback(() => {
    setStats(null);
    setEstimate(null);
    setComparison(null); 
    setForecast(null);
    setInsights(null);
    setAiInsights(null);
    setSellerInsights(null);
    setListingGuidance(null);
    setSellerScore(null);
    setError(null);
    setAiError(null);
  }, []);

  return {
    stats,
    estimate,
    comparison,
    forecast,
    insights,
    aiInsights,
    sellerInsights,
    listingGuidance,
    sellerScore,
    isLoading,
    aiLoading,
    error,
    aiError,
    getMarketStats,
    estimateProperty,
    compareLocations,
    getPriceForecast,
    getMarketInsights,
    getAIInsights,
    getSellerInsights,
    getListingPriceGuidance,
    getSellerScore,
    clearAll,
  };
};
